import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Upload, ClipboardPaste, Send, Eye, UserPlus, Search, X, ExternalLink } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const blank = { name: "", description: "" };

/* ─────────────────── Existing-contact picker (reusable) ─────────────────── */
function SelectExistingContacts({ excludeInList, selectedIds, onChange }) {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [city, setCity] = useState("");
  const [optIn, setOptIn] = useState("any");   // any | in | out
  const [dnd, setDnd] = useState("any");        // any | dnd | free
  const [loading, setLoading] = useState(false);

  useEffect(() => { setLoading(true); api.get("/contacts").then(r => setContacts(r.data)).finally(() => setLoading(false)); }, []);

  const filtered = useMemo(() => {
    return contacts.filter(c => {
      if (excludeInList && (c.list_ids || []).includes(excludeInList)) return false;
      if (q) {
        const s = q.toLowerCase();
        if (!(c.name || "").toLowerCase().includes(s) && !(c.phone || "").includes(q) && !(c.email || "").toLowerCase().includes(s)) return false;
      }
      if (tag && !(c.tags || []).some(t => t.toLowerCase() === tag.toLowerCase())) return false;
      if (city && (c.custom_fields?.city || "").toLowerCase() !== city.toLowerCase()) return false;
      if (optIn === "in" && c.opted_out) return false;
      if (optIn === "out" && !c.opted_out) return false;
      if (dnd === "dnd" && !c.dnd) return false;
      if (dnd === "free" && c.dnd) return false;
      return true;
    });
  }, [contacts, excludeInList, q, tag, city, optIn, dnd]);

  const tagOptions = useMemo(() => Array.from(new Set(contacts.flatMap(c => c.tags || []))).sort(), [contacts]);
  const cityOptions = useMemo(() => Array.from(new Set(contacts.map(c => c.custom_fields?.city).filter(Boolean))).sort(), [contacts]);

  const toggle = (id) => {
    const set = new Set(selectedIds);
    if (set.has(id)) set.delete(id); else set.add(id);
    onChange(Array.from(set));
  };
  const selectAllFiltered = () => onChange(Array.from(new Set([...selectedIds, ...filtered.map(c => c.id)])));
  const clearSelection = () => onChange([]);

  return (
    <div className="space-y-2" data-testid="select-existing-contacts">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="col-span-2 relative">
          <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / phone / email" className="rounded-sm pl-7 h-8 text-xs" data-testid="sec-search" />
        </div>
        <select value={tag} onChange={e => setTag(e.target.value)} className="h-8 px-2 rounded-sm border bg-background text-xs" data-testid="sec-tag">
          <option value="">All tags</option>
          {tagOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={city} onChange={e => setCity(e.target.value)} className="h-8 px-2 rounded-sm border bg-background text-xs" data-testid="sec-city">
          <option value="">All cities</option>
          {cityOptions.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="flex gap-1">
          <select value={optIn} onChange={e => setOptIn(e.target.value)} className="h-8 px-2 rounded-sm border bg-background text-xs flex-1" data-testid="sec-optin">
            <option value="any">Opt: any</option>
            <option value="in">Opted-in</option>
            <option value="out">Opted-out</option>
          </select>
          <select value={dnd} onChange={e => setDnd(e.target.value)} className="h-8 px-2 rounded-sm border bg-background text-xs flex-1" data-testid="sec-dnd">
            <option value="any">DND: any</option>
            <option value="dnd">DND</option>
            <option value="free">Not DND</option>
          </select>
        </div>
      </div>

      <div className="flex items-center justify-between text-xs">
        <div className="text-muted-foreground">
          <span data-testid="sec-selected-count">{selectedIds.length}</span> selected · {filtered.length} match filter · {contacts.length} total
        </div>
        <div className="flex gap-1">
          <Button type="button" variant="outline" size="sm" className="h-7 rounded-sm text-[10px]" onClick={selectAllFiltered} data-testid="sec-select-all">Select all filtered</Button>
          <Button type="button" variant="ghost" size="sm" className="h-7 rounded-sm text-[10px]" onClick={clearSelection} data-testid="sec-clear">Clear</Button>
        </div>
      </div>

      <div className="border rounded-sm max-h-72 overflow-y-auto">
        {loading && <div className="text-xs text-muted-foreground p-3 text-center">Loading contacts…</div>}
        {!loading && filtered.length === 0 && <div className="text-xs text-muted-foreground p-3 text-center">No contacts match the filter.</div>}
        <table className="w-full text-xs">
          <tbody>
            {filtered.map(c => {
              const sel = selectedIds.includes(c.id);
              return (
                <tr key={c.id} className={`border-b hover:bg-muted/30 cursor-pointer ${sel ? "bg-primary/5" : ""}`} onClick={() => toggle(c.id)} data-testid={`sec-row-${c.id}`}>
                  <td className="px-2 py-1.5 w-8"><input type="checkbox" checked={sel} onChange={() => toggle(c.id)} onClick={e => e.stopPropagation()} data-testid={`sec-check-${c.id}`} /></td>
                  <td className="px-2 py-1.5 font-medium">{c.name}</td>
                  <td className="px-2 py-1.5 font-mono text-muted-foreground">{c.phone}</td>
                  <td className="px-2 py-1.5 text-muted-foreground truncate max-w-[10rem]">{c.email || "—"}</td>
                  <td className="px-2 py-1.5">
                    <div className="flex gap-1 flex-wrap">
                      {(c.tags || []).slice(0, 2).map(t => <Badge key={t} variant="outline" className="rounded-sm text-[9px] h-4 px-1">{t}</Badge>)}
                      {c.dnd && <Badge variant="outline" className="rounded-sm text-[9px] h-4 px-1 border-orange-300 text-orange-700">DND</Badge>}
                      {c.opted_out && <Badge variant="outline" className="rounded-sm text-[9px] h-4 px-1 border-red-300 text-red-700">Opt-out</Badge>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ─────────────────── View Members Drawer (list detail) ─────────────────── */
function ViewMembersDrawer({ list, open, onOpenChange, onChanged }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [pickIds, setPickIds] = useState([]);

  const load = async () => {
    if (!list) return;
    setLoading(true);
    try {
      const params = q ? `?q=${encodeURIComponent(q)}` : "";
      const r = await api.get(`/lists/${list.id}/members${params}`);
      setMembers(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open && list) load(); }, [open, list, q]);

  const removeOne = async (contactId) => {
    if (!window.confirm("Remove this contact from the list? (The contact will remain in your master Contacts.)")) return;
    try {
      await api.delete(`/lists/${list.id}/members/${contactId}`);
      toast.success("Removed from list");
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const attachSelected = async () => {
    if (!pickIds.length) { toast.error("Pick at least one contact"); return; }
    try {
      const r = await api.post(`/lists/${list.id}/members/attach`, { contact_ids: pickIds });
      toast.success(`${r.data.attached} attached${r.data.already_in_list ? `, ${r.data.already_in_list} already in list` : ""}`);
      setShowAdd(false); setPickIds([]);
      load(); onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="view-members-drawer">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" /> {list?.name}
            <Badge variant="outline" className="rounded-sm text-[10px] font-mono">{members.length} contacts</Badge>
          </DialogTitle>
          {list?.description && <div className="text-xs text-muted-foreground">{list.description}</div>}
        </DialogHeader>

        <div className="flex items-center justify-between gap-2 flex-wrap py-2 border-y">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search in list…" className="rounded-sm pl-7 h-8 text-xs" data-testid="vm-search" />
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="rounded-sm gap-1" onClick={() => setShowAdd(true)} data-testid="vm-add-existing">
              <UserPlus className="h-3.5 w-3.5" /> Add existing contacts
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto border rounded-sm">
          {loading && <div className="text-xs text-muted-foreground p-4 text-center">Loading…</div>}
          {!loading && members.length === 0 && <div className="text-xs text-muted-foreground p-6 text-center">No contacts in this list yet.</div>}
          {members.length > 0 && (
            <table className="w-full text-xs" data-testid="vm-table">
              <thead className="bg-muted/40 sticky top-0">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2">Name</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Tags</th>
                  <th className="px-3 py-2">City</th>
                  <th className="px-3 py-2">Opt-in</th>
                  <th className="px-3 py-2">DND</th>
                  <th className="px-3 py-2">Added</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map(c => (
                  <tr key={c.id} className="border-t hover:bg-muted/20" data-testid={`vm-row-${c.id}`}>
                    <td className="px-3 py-1.5 font-medium">{c.name}</td>
                    <td className="px-3 py-1.5 font-mono text-muted-foreground">{c.phone}</td>
                    <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[10rem]">{c.email || "—"}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex flex-wrap gap-0.5">
                        {(c.tags || []).slice(0, 3).map(t => <Badge key={t} variant="outline" className="rounded-sm text-[9px] h-4 px-1">{t}</Badge>)}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground">{c.custom_fields?.city || "—"}</td>
                    <td className="px-3 py-1.5">
                      {c.opted_out
                        ? <Badge variant="outline" className="rounded-sm text-[9px] h-4 px-1 border-red-300 text-red-700">Opted-out</Badge>
                        : <Badge variant="outline" className="rounded-sm text-[9px] h-4 px-1 border-emerald-300 text-emerald-700">Opted-in</Badge>}
                    </td>
                    <td className="px-3 py-1.5">
                      {c.dnd ? <Badge variant="outline" className="rounded-sm text-[9px] h-4 px-1 border-orange-300 text-orange-700">DND</Badge> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-1.5 text-muted-foreground font-mono text-[10px]">{(c.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center justify-end gap-0.5">
                        <Link to={`/contacts/${c.id}`}>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 rounded-sm" title="View contact" data-testid={`vm-view-${c.id}`}>
                            <Eye className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                        </Link>
                        <Link to={`/contacts/${c.id}`}>
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 rounded-sm" title="Edit contact" data-testid={`vm-edit-${c.id}`}>
                            <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                          </Button>
                        </Link>
                        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 rounded-sm" title="Remove from list (keeps contact)" onClick={() => removeOne(c.id)} data-testid={`vm-remove-${c.id}`}>
                          <X className="h-3.5 w-3.5 text-red-600" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="text-[10px] text-muted-foreground pt-1 border-t">
          <strong>Note:</strong> Removing a contact here only strips list membership. Permanent deletion is available from the contact&apos;s detail page.
        </div>

        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add existing contacts to &quot;{list?.name}&quot;</DialogTitle></DialogHeader>
            <SelectExistingContacts excludeInList={list?.id} selectedIds={pickIds} onChange={setPickIds} />
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowAdd(false); setPickIds([]); }}>Cancel</Button>
              <Button onClick={attachSelected} className="rounded-sm gap-1" data-testid="vm-add-submit">
                <UserPlus className="h-3.5 w-3.5" /> Add {pickIds.length} contact{pickIds.length === 1 ? "" : "s"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

/* ─────────────────── Lists page ─────────────────── */
export default function Lists() {
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [counts, setCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [populate, setPopulate] = useState("skip"); // skip | existing | file | paste
  const [existingIds, setExistingIds] = useState([]);
  const [pasteText, setPasteText] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    const r = await api.get("/lists");
    setLists(r.data);
    const c = await api.get("/contacts");
    const m = {};
    for (const x of c.data) for (const id of (x.list_ids || [])) m[id] = (m[id] || 0) + 1;
    setCounts(m);
  };
  useEffect(() => { load(); }, []);

  const resetDialog = () => {
    setForm(blank); setEditing(null); setPopulate("skip");
    setPasteText(""); setFile(null); setExistingIds([]);
    if (fileRef.current) fileRef.current.value = "";
  };
  const openCreate = () => { resetDialog(); setOpen(true); };
  const openEdit = (l) => { resetDialog(); setEditing(l); setForm({ name: l.name, description: l.description || "" }); setOpen(true); };

  const populateMembers = async (listId) => {
    if (populate === "existing" && existingIds.length) {
      const r = await api.post(`/lists/${listId}/members/attach`, { contact_ids: existingIds });
      toast.success(`${r.data.attached} contacts added`);
    } else if (populate === "file" && file) {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/lists/${listId}/members/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.inserted} added, ${r.data.attached_existing} attached${r.data.error_count ? `, ${r.data.error_count} errors` : ""}`);
    } else if (populate === "paste" && pasteText.trim()) {
      const r = await api.post(`/lists/${listId}/members/paste`, { text: pasteText });
      toast.success(`${r.data.inserted} added, ${r.data.attached_existing} attached`);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("List name is required"); return; }
    setSaving(true);
    try {
      let listId;
      if (editing) {
        await api.patch(`/lists/${editing.id}`, form);
        listId = editing.id;
      } else {
        const r = await api.post("/lists", form);
        listId = r.data.id;
        toast.success("List created");
      }
      if (populate !== "skip") {
        try { await populateMembers(listId); }
        catch (err) { toast.error(err.response?.data?.detail || "Failed to add members"); }
      } else if (editing) {
        toast.success("List updated");
      }
      setOpen(false); resetDialog(); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const del = async (l) => {
    if (!window.confirm(`Delete list "${l.name}"?\n\nThis only removes the list and its memberships. Contacts stay in your master Contacts.`)) return;
    try {
      const r = await api.delete(`/lists/${l.id}`);
      toast.success(`List deleted (${r.data.detached_contacts} contacts detached, but still in Contacts)`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const startCampaign = (l) => navigate(`/campaigns?new=1&list_id=${l.id}`);
  const openView = (l) => setViewing(l);

  return (
    <div className="space-y-4" data-testid="lists-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Audience Segments</div>
          <h1 className="text-3xl font-black tracking-tighter">Contact Lists</h1>
          <div className="text-xs text-muted-foreground mt-1">Create a list, add contacts, then send campaigns via WhatsApp, SMS, or Voice.</div>
        </div>
        <Button className="rounded-sm gap-2" onClick={openCreate} data-testid="new-list-button">
          <Plus className="h-4 w-4" /> New List
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {lists.map(l => (
          <Card key={l.id} className="rounded-sm shadow-none" data-testid={`list-card-${l.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="font-semibold truncate">{l.name}</span>
                </div>
                <span className="font-mono text-xs text-muted-foreground shrink-0" data-testid={`list-count-${l.id}`}>{counts[l.id] || 0} contacts</span>
              </div>
              <div className="text-xs text-muted-foreground min-h-[2.5rem]">{l.description || "—"}</div>
              <div className="flex justify-between items-center pt-1 gap-1 flex-wrap">
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => openView(l)} data-testid={`view-list-${l.id}`}>
                    <Eye className="h-3 w-3" /> View
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-sm gap-1" onClick={() => startCampaign(l)} data-testid={`send-campaign-${l.id}`}>
                    <Send className="h-3 w-3" /> Campaign
                  </Button>
                </div>
                <div className="flex gap-0.5">
                  <Button variant="ghost" size="sm" className="rounded-sm gap-1" onClick={() => openEdit(l)} data-testid={`edit-list-${l.id}`}>
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="text-red-600 rounded-sm gap-1" onClick={() => del(l)} data-testid={`delete-list-${l.id}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {lists.length === 0 && <div className="text-sm text-muted-foreground">No lists yet.</div>}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetDialog(); }}>
        <DialogContent className="rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit List" : "New List"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="list-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm" data-testid="list-description-input" />
            </div>

            <div className="pt-2 border-t">
              <Label className="text-xs uppercase tracking-wide text-muted-foreground">Add Contacts to this List</Label>
              <Tabs value={populate} onValueChange={setPopulate} className="mt-2">
                <TabsList className="grid grid-cols-4 rounded-sm">
                  <TabsTrigger value="skip" className="rounded-sm text-xs" data-testid="populate-tab-skip">Skip</TabsTrigger>
                  <TabsTrigger value="existing" className="rounded-sm text-xs gap-1" data-testid="populate-tab-existing"><Users className="h-3 w-3" /> Existing</TabsTrigger>
                  <TabsTrigger value="file" className="rounded-sm text-xs gap-1" data-testid="populate-tab-file"><Upload className="h-3 w-3" /> Upload</TabsTrigger>
                  <TabsTrigger value="paste" className="rounded-sm text-xs gap-1" data-testid="populate-tab-paste"><ClipboardPaste className="h-3 w-3" /> Paste</TabsTrigger>
                </TabsList>
                <TabsContent value="skip" className="text-xs text-muted-foreground pt-2">
                  {editing ? "Members will not be changed." : "Create an empty list — add contacts later."}
                </TabsContent>
                <TabsContent value="existing" className="pt-2">
                  <SelectExistingContacts excludeInList={editing?.id} selectedIds={existingIds} onChange={setExistingIds} />
                </TabsContent>
                <TabsContent value="file" className="space-y-2 pt-2">
                  <div className="text-xs text-muted-foreground">CSV or Excel (.xlsx) with columns: <code>name, phone, email, tags</code>. Existing contacts are re-used.</div>
                  <Input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={e => setFile(e.target.files?.[0] || null)} className="rounded-sm" data-testid="list-file-input" />
                  {file && <div className="text-xs">Selected: <span className="font-mono">{file.name}</span> ({(file.size/1024).toFixed(1)} KB)</div>}
                </TabsContent>
                <TabsContent value="paste" className="space-y-2 pt-2">
                  <div className="text-xs text-muted-foreground">Paste phone numbers or emails — one per line, or comma-separated.</div>
                  <Textarea rows={6} placeholder={"+919876543210\n+919812345678\nrahul@example.com"} value={pasteText} onChange={e => setPasteText(e.target.value)} className="rounded-sm font-mono text-xs" data-testid="list-paste-textarea" />
                </TabsContent>
              </Tabs>
            </div>

            <DialogFooter>
              <Button type="submit" className="rounded-sm" disabled={saving} data-testid="save-list-button">
                {saving ? "Saving…" : (editing ? "Save" : "Create List")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ViewMembersDrawer list={viewing} open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }} onChanged={load} />
    </div>
  );
}
